import { IPortalUser, ExtraFeature } from './types';
import { UserApi, ILoginResponseViewModel, APIPermissionList, APIPermission, APIPermissionViewModel } from '../Api';
import { EventEmitter, identifyUserPilot, removeUserPilot, isInternalLogin } from '../../../utils';
import { CompanyService } from '../../Company';
import { backendServices } from '../..';

type CompanyChildOption = {
    value: number;
    label: string;
};

// TODO: remove when new brand is fully deployed
export type BrandVersion = 'transcepta' | 'newBrand';

export class PortalUserService {
    private api = new UserApi();

    private emitter = new EventEmitter<IPortalUser | null>();

    private currentUser: IPortalUser | null = null;

    public subscribe = this.emitter.subscribe.bind(this.emitter);

    private companyChildrenEventEmitter = new EventEmitter<{
        companyChildren: CompanyChildOption[] | null;
        selectedChild: CompanyChildOption | null;
        selectedChildren: CompanyChildOption[] | null;
    }>();

    public subscribeToCompanyChildrenEvent = this.companyChildrenEventEmitter.subscribe.bind(
        this.companyChildrenEventEmitter
    );

    private companyChildren: CompanyChildOption[] | null = null;

    private companyChildrenSelection: {
        selectedChild: CompanyChildOption | null;
        selectedChildren: CompanyChildOption[] | null;
    } = {
        selectedChild: null,
        selectedChildren: null,
    };

    public setCompanyChildren(
        companyChildrenVM: backendServices.ViewModels.BuyerPortalSettingsViewModel['ChildCompanies']
    ) {
        this.companyChildrenSelection = { selectedChild: null, selectedChildren: null };

        if (companyChildrenVM == null) {
            this.companyChildren = [];
            return;
        }

        this.companyChildren = companyChildrenVM.map((vm) => ({
            value: vm.Id,
            label: vm.Name,
        }));

        this.companyChildrenEventEmitter.notify({
            companyChildren: this.companyChildren,
            ...this.getCompanyChildrenSelection(),
        });
    }

    public getCompanyChildren() {
        if (this.companyChildren === null) {
            return [];
        }

        return this.companyChildren;
    }

    public setCompanyChildrenSelection(selectedChildId: number | null) {
        const selectedChild = this.getCompanyChildren().find((child) => child.value === selectedChildId);

        if (!selectedChild && selectedChildId !== null) {
            throw new Error('Attempt to set selectedCompanyChild using a non-valid id');
        }

        this.companyChildrenSelection.selectedChild = selectedChild ?? null;

        this.companyChildrenEventEmitter.notify({
            companyChildren: this.companyChildren,
            ...this.getCompanyChildrenSelection(),
        });
    }

    public setMultipleCompanyChildrenSelection(selectedChildIds: number[] | null) {
        const selectedChildren = selectedChildIds
            ? this.getCompanyChildren().filter((child) => selectedChildIds.includes(child.value))
            : null;

        if (!selectedChildIds && selectedChildIds !== null) {
            throw new Error('Attempt to set selectedCompanyChildren using non-valid ids');
        }

        this.companyChildrenSelection.selectedChildren = selectedChildren;

        this.companyChildrenEventEmitter.notify({
            companyChildren: this.companyChildren,
            ...this.getCompanyChildrenSelection(),
        });
    }

    public getCompanyChildrenSelection() {
        if (this.companyChildren === null) {
            return { selectedChild: null, selectedChildren: null };
        }

        return this.companyChildrenSelection;
    }

    public getSelectedCompanyChild() {
        return this.getCompanyChildrenSelection().selectedChild;
    }

    public getSelectedCompanyChildren() {
        return this.getCompanyChildrenSelection().selectedChildren;
    }

    public clearCompanyChildrenState() {
        this.setCompanyChildren(null);
        this.setCompanyChildrenSelection(null);
        this.setMultipleCompanyChildrenSelection(null);
    }

    public clearSelectedCompanyChild() {
        this.setCompanyChildrenSelection(null);
    }

    public clearSelectedCompanyChildren() {
        this.setMultipleCompanyChildrenSelection(null);
    }

    public getParentAndChildCompanyIds(): number[] {
        const parentId = this.getCurrentCompanyId();
        const childIds = this.getCompanyChildren().map((child) => child.value);

        return [parentId, ...childIds];
    }

    /**
     * Returns the current PortalUser or null if no user is logged in.
     */
    public getCurrentUser<T extends 'mustBeLoggedIn' | undefined>(
        mode?: T
    ): T extends 'mustBeLoggedIn' ? IPortalUser : IPortalUser | null {
        const user = this.currentUser;

        if (mode === 'mustBeLoggedIn' && !user) {
            throw new Error('User must be logged in');
        }

        return user as any;
    }

    /**
     * Returns the company id the current user is currently logged in under.
     */
    public getCurrentCompanyId() {
        const user = this.getCurrentUser('mustBeLoggedIn');

        if (!user.currentCompanyId) {
            throw new Error('User must be logged in under a company id');
        }

        return user.currentCompanyId;
    }

    /**
     * Returns the full company data for the company the current user is logged in under.
     * Returns null if no company data is available.
     */
    public getCurrentCompany() {
        const user = this.getCurrentUser('mustBeLoggedIn');
        return user.currentCompany || null;
    }

    /**
     * Clears the current user. Call during log out.
     */
    public clearCurrentUser() {
        this.setCurrentUser(null);
        removeUserPilot();
    }

    /**
     * Load a Buyer Portal PortalUser as the current user. Call during log in.
     */
    public async loadBuyerPortalUser(response: ILoginResponseViewModel): Promise<void> {
        const user = await this.getPortalUser(response);
        await this.determineBuyerPortalUserFeatures(user);
        const companyApi = new backendServices.Apis.CompanyApi();

        // use current company id if available (internal login)
        const companyIdToFetch = user.currentCompanyId || user.companyId;
        const company = companyIdToFetch ? await companyApi.getCompany(companyIdToFetch) : null;
        const companyName = company?.data[0]?.Name ?? '';

        // Store the full company data
        if (company?.data[0]) {
            user.currentCompany = company.data[0];
        }

        this.setCurrentUser(user);
        const userRoles = user.userRoles.map((role) => role.RoleID);
        identifyUserPilot(user.id, {
            companyID: user.companyId?.toString() ?? '',
            companyName: companyName,
            name: user.name,
            username: user.userName,
            userRoles: userRoles,
        });
    }

    /**
     * Load a Supplier Portal PortalUser as the current user. Call during log in.
     */
    public async loadSupplierPortalUser(response: ILoginResponseViewModel): Promise<void> {
        const user = await this.getPortalUser(response);
        await this.determineSupplierPortalUserFeatures(user);
        const companyApi = new backendServices.Apis.CompanyApi();

        // use current company id if available (internal login)
        const companyIdToFetch = user.currentCompanyId || user.companyId;
        const company = companyIdToFetch ? await companyApi.getCompany(companyIdToFetch) : null;
        const companyName = company?.data[0]?.Name ?? '';

        // Store the full company data
        if (company?.data[0]) {
            user.currentCompany = company.data[0];
        }

        this.setCurrentUser(user);
        const userRoles = user.userRoles.map((role) => role.RoleID);
        identifyUserPilot(user.id, {
            companyID: user.companyId?.toString() ?? '',
            companyName: companyName,
            name: user.name,
            username: user.userName,
            userRoles: userRoles,
        });
    }

    /**
     * Load a Customer Service Portal PortalUser as the current user. Call during log in.
     */
    public async loadCustomerServicePortalUser(response: ILoginResponseViewModel): Promise<void> {
        const user = await this.getPortalUser(response);
        await this.determineCustomerServicePortalUserFeatures(user);
        this.setCurrentUser(user);
    }

    private getUserPermissionKeys(response: APIPermissionViewModel[]): readonly APIPermission[] {
        return response
            .map((permission) => permission.Name)
            .filter((permissionKey): permissionKey is APIPermission =>
                APIPermissionList.includes(permissionKey as APIPermission)
            );
    }

    private async getPortalUser(loginResponse: ILoginResponseViewModel) {
        const userId = Number(loginResponse.userId);
        const response = await this.api.getUser(userId);
        const userViewModel = response.data[0];

        const { data: permissionData } = await this.api.getPermissionsForUser(userId);

        if (!userViewModel) {
            throw new Error(`Expected user to be returned for ${userId}`);
        }

        let apiPermissions = new Set(this.getUserPermissionKeys(permissionData));

        // if internal login, show all permissions except HIDE_DASHBOARD (internal users should always see the dashboard) and TM_PARKING_LOT_ONLY_MODE_VIEW (internal users should never be in parking lot only mode)
        if (isInternalLogin()) {
            apiPermissions = new Set(
                APIPermissionList.filter((p) => p !== 'HIDE_DASHBOARD' && p !== 'TM_PARKING_LOT_ONLY_MODE_VIEW')
            );
        }

        apiPermissions.add('TM_CONFIGURABLE_WORKFLOWS_VIEW');
        apiPermissions.add('TM_CONFIGURABLE_WORKFLOWS_EDIT');
        const user: IPortalUser = {
            id: userViewModel.ID,
            companyId: userViewModel.CompanyID,
            currentCompanyId: Number(loginResponse.companyId),
            currentCompany: null, // Will be populated during login
            managersUserId: userViewModel.ManagersUserId ?? null,
            name: userViewModel.Name,
            userName: userViewModel.UserName,
            email: userViewModel.Email,
            phone: userViewModel.Phone,
            userRoles: userViewModel.UserRoles,
            apiPermissions,
            extraFeatures: new Set<ExtraFeature>(),
        };

        return user;
    }

    private async determineBuyerPortalUserFeatures(user: IPortalUser) {
        const companyService = new CompanyService();

        // determine if VendorManagement feature is enabled
        const vendorManagementFlags = await companyService.getVendorManagementFlags(user.currentCompanyId!);
        if (vendorManagementFlags.supplierReferenceTable === 'vendor') {
            user.extraFeatures.add('FE_TM_VENDOR_MANAGEMENT_ENABLED');
        }
        if (vendorManagementFlags.supplierReferenceTable === 'vendor' && vendorManagementFlags.enableVendorEdits) {
            user.extraFeatures.add('FE_TM_VENDOR_MANAGEMENT_EDIT_ENABLED');
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    private async determineSupplierPortalUserFeatures(user: IPortalUser) {
        // keep this empty for now. We can add supplier specific features here in the future.
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    private async determineCustomerServicePortalUserFeatures(user: IPortalUser) {}

    private setCurrentUser(user: IPortalUser | null) {
        this.currentUser = user;
        this.emitter.notify(this.currentUser);
    }

    /** Methods to aid in automated testing */

    /**
     * Calls a modifier function with the current user instance to allow changing user
     * properties for tests / debugging purposes.
     *
     * This can be used to quickly assign granular permissions to a user or extra features to
     * a user to see how the application behaves in different cases.
     *
     * @param modifier function to modify the current user instance
     */
    public modifyUserForTest(modifier: (user: IPortalUser) => void): void {
        const user = this.currentUser;

        if (!user) {
            return;
        }

        const newUser = {
            ...user,
            apiPermissions: new Set(user.apiPermissions),
            extraFeatures: new Set(user.extraFeatures),
        };

        modifier(newUser);

        this.setCurrentUser(newUser);
    }

    /**
     * Temporary method to load fake Customer Service Portal PortalUser until authentication
     * for the portal is implemented.
     */
    public loadFakeCustomerServicePortalUser() {
        const setWithEverything = new Set<any>();
        setWithEverything.has = () => true;

        this.setCurrentUser({
            id: 1,
            companyId: null,
            currentCompanyId: null,
            currentCompany: null,
            managersUserId: null,
            name: 'Transcepta User',
            userName: 'transcepta-user',
            email: 'transcepta-user@transcepta.com',
            phone: '555-555-5555',
            extraFeatures: setWithEverything,
            apiPermissions: setWithEverything,
            userRoles: [],
        });
    }

    /**
     * Creates a fake user for a test and runs the modifier to modify its attributes.
     * @param modifier function to modify the fake user
     */
    public loadUserForTest(modifier: (user: IPortalUser) => void) {
        this.loadFakeCustomerServicePortalUser();
        this.modifyUserForTest(modifier);
    }

    // TODO: remove all brand version related code when new brand is fully deployed
    // Brand version state
    private brandVersion: BrandVersion = 'transcepta';

    private brandEmitter = new EventEmitter<BrandVersion>();

    public subscribeToBrand = this.brandEmitter.subscribe.bind(this.brandEmitter);

    public setBrandVersion(brand: BrandVersion): void {
        // set brand, update cache
        this.brandVersion = brand;
        document.documentElement.dataset.brand = brand;
        localStorage.setItem('cached_brand_version', brand);
        this.brandEmitter.notify(brand);
    }

    public getBrandVersion(): BrandVersion {
        // On initial load, check cache first
        if (this.brandVersion === 'transcepta') {
            const cached = localStorage.getItem('cached_brand_version') as BrandVersion | null;
            if (cached && (cached === 'transcepta' || cached === 'newBrand')) {
                this.brandVersion = cached;
                document.documentElement.dataset.brand = cached;
            }
        }
        return this.brandVersion;
    }

    // clear brand version from local storage, reset to transcepta
    public clearBrandVersion(): void {
        localStorage.removeItem('cached_brand_version');
        this.brandVersion = 'transcepta';
        document.documentElement.dataset.brand = 'transcepta';
        this.brandEmitter.notify('transcepta');
    }
}

/**
 * The API for managing current logged in user state on our portals.
 */
export const portalUserService = new PortalUserService();

window.transceptaPortalUserService = portalUserService;

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        transceptaPortalUserService: PortalUserService;
    }
}
